/**
 * ConnectBankButton.jsx — launches Plaid Link.
 *
 * Two modes:
 *   default  — connect a new institution
 *   update   — re-authenticate an existing connection whose login expired
 *              (pass connectionId). Reuses the same Item, so the sync cursor
 *              and all existing transactions survive.
 *
 * Bank credentials are entered inside Plaid's iframe and never reach us; we
 * only ever see the short-lived public_token Link hands back.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import { usePlaidLink } from 'react-plaid-link';
import { useCreateLinkToken, useExchangePublicToken } from '../../hooks/usePlaid';
import { useToast } from '../layout/Toast';

export default function ConnectBankButton({
  connectionId = null,
  label = 'Connect a bank',
  variant = 'contained',
  size = 'small',
  disabled = false,
}) {
  const [linkToken, setLinkToken] = useState(null);
  const createToken = useCreateLinkToken();
  const exchange = useExchangePublicToken();
  const { toast } = useToast();

  const onSuccess = useCallback(async (publicToken) => {
    // Update mode re-authorizes the existing Item — there's no new token to
    // exchange, and doing so would create a duplicate connection.
    if (connectionId) {
      toast('Bank reconnected');
      setLinkToken(null);
      return;
    }
    try {
      const res = await exchange.mutateAsync(publicToken);
      toast(
        res.syncError
          ? `Connected ${res.institutionName || 'bank'}, but the first sync failed: ${res.syncError}`
          : `Connected ${res.institutionName || 'bank'} — ${res.sync?.added ?? 0} transactions imported`,
        res.syncError ? 'error' : 'success'
      );
    } catch (e) {
      toast(e.message || 'Could not finish connecting', 'error');
    } finally {
      setLinkToken(null);
    }
  }, [connectionId, exchange, toast]);

  const onExit = useCallback((err) => {
    // A user closing the modal is not an error worth shouting about.
    if (err) toast(err.display_message || err.error_message || 'Bank connection cancelled', 'error');
    setLinkToken(null);
  }, [toast]);

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess, onExit });

  // Link tokens are short-lived, so one is minted per click and opened as soon
  // as Link reports ready rather than being held in state ahead of time.
  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  const handleClick = async () => {
    try {
      const res = await createToken.mutateAsync(connectionId);
      setLinkToken(res.linkToken);
    } catch (e) {
      toast(e.message || 'Could not start the bank connection', 'error');
    }
  };

  const busy = createToken.isPending || exchange.isPending;

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleClick}
      disabled={disabled || busy}
      startIcon={busy ? <CircularProgress size={14} /> : <AccountBalanceRoundedIcon sx={{ fontSize: 18 }} />}
      sx={{ textTransform: 'none' }}
    >
      {busy ? 'Opening…' : label}
    </Button>
  );
}
